import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { fetchNotificationsForNames, markNotificationRead, markNotificationsRead, fetchNotificationById, updateNotificationDeletedBy, deleteNotifications } from './lib/notificationsData';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseQuery } from './lib/supabaseQuery';
import { Bell, MessageSquare, CheckCircle2, X, Search, Trash2, Square, CheckSquare, ChevronDown, ChevronUp, SlidersHorizontal, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/** Minimal user shape consumed by the notification widgets (AuthedApp passes the full AppUser). */
interface NotificationUser {
  name?: string;
  role?: string;
  tenantId?: string | null;
}

/** One notifications-table row (plus legacy jsonb aliases) rendered by the widgets. */
interface NotificationRow {
  id: string;
  read?: boolean;
  fromUserName?: string;
  text?: string;
  body?: string;
  traceTitle?: string;
  traceId?: string;
  type?: string;
  created_at?: string;
  deletedBy?: string[];
  data?: {
    fromUserName?: string;
    text?: string;
    traceTitle?: string;
    traceId?: string;
    type?: string;
    entityType?: string;
    deletedBy?: string[];
    [key: string]: unknown;
  };
}



import { ACTIVITY_CATEGORIES, getActivityCategory, type ActivityCategory } from './lib/notificationCategories';
/**
 * Listener component that monitors Firestore for notifications mentioning the current user.
 * Displays temporary toast-style notifications at the top-right of the screen.
 * 
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.currentUser - The currently logged-in user.
 * @param {string} props.currentUser.name - The full name of the user used for mention matching.
 * @returns {React.ReactPortal|null} A portal to document.body containing notifications or null.
 */
export function NotificationListener({ currentUser }: { currentUser: NotificationUser }) {
  const { t } = useTranslation();
  const activeTenantId = currentUser?.tenantId;
  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  // Expanded toast: one toast can show its FULL text (chevron toggle). The
  // 6s auto-dismiss still applies — expanding does not freeze the toast.
  const [expandedToastId, setExpandedToastId] = useState<string | null>(null);

  /**
   * Marks a specific notification as read in Firestore.
   * 
   * @param {string} id - The Firestore document ID of the notification.
   * @returns {Promise<void>}
   */
  const markAsRead = async (id: string) => {
    await markNotificationRead(activeTenantId, id);
  }

  useEffect(() => {
    if (!currentUser?.name || !activeTenantId) return;
    const nameLower = currentUser.name.toLowerCase();
    const nameSpace = currentUser.name.split(" ")[0].toLowerCase();
    const nameDot = currentUser.name.split(".")[0].toLowerCase();
    const roleLower = (currentUser.role || "").toLowerCase();
    const possibleNames = [...new Set([nameDot, nameLower, nameSpace, roleLower])].filter(Boolean);

    // Initial fetch
    fetchNotificationsForNames(activeTenantId, possibleNames)
      .then(({ data }) => {
        if (data) {
          const now = Date.now();
          const newNots: NotificationRow[] = [];
          data.forEach((notif) => {
            const notTime = notif.created_at ? new Date(notif.created_at).getTime() : now;
            const timeDiffSeconds = Math.floor((now - notTime) / 1000);
            if (timeDiffSeconds < 120 && notif.fromUserName !== currentUser.name) {
              newNots.push({ id: notif.id, ...notif });
            }
          });
          if (newNots.length > 0) {
            setNotifications(prev => [...prev, ...newNots]);
            setTimeout(() => {
              setNotifications(prev => prev.filter(n => !newNots.find(nn => nn.id === n.id)));
            }, 6000);
          }
        }
      });

    // Realtime handled by centralized realtimeStore
  }, [currentUser, activeTenantId]);

  if (notifications.length === 0) return null;

  return createPortal(
    <div className='fixed top-6 right-6 z-999999 flex flex-col gap-3 pointer-events-none'>
      {notifications.map(n => (
        <div key={n.id} className='bg-surface/95 shadow-2xl rounded-2xl p-4 w-[340px] pointer-events-auto animate-in slide-in-from-right-8 fade-in border border-border'>
          <div className='flex justify-between items-start mb-1.5'>
            <span className='text-[10px] font-bold text-accent uppercase tracking-widest flex items-center gap-1.5'>
              <MessageSquare size={12} /> {t('notifications.newMessage', 'Neue Nachricht')}
            </span>
            <button onClick={() => setNotifications(prev => prev.filter(item => item.id !== n.id))} className='text-muted hover:text-fg transition-all'><X size={14} /></button>
          </div>
          <p className='text-sm font-bold text-fg mb-1'>{n.fromUserName || n.data?.fromUserName || t('common.system', 'System')}</p>
          <div className='flex items-start gap-1'>
            <p className={`text-xs text-muted leading-relaxed font-medium flex-1 ${expandedToastId === n.id ? '' : 'line-clamp-2'}`}>"{n.text || n.body || n.data?.text}"</p>
            {(n.text || n.body || n.data?.text || '').length > 90 && (
              <button
                onClick={() => setExpandedToastId(expandedToastId === n.id ? null : n.id)}
                className='text-muted hover:text-fg transition-all shrink-0 mt-0.5'
                title={expandedToastId === n.id ? t('common.collapse', 'Weniger') : t('common.expand', 'Mehr')}
              >
                {expandedToastId === n.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            )}
          </div>
          <div className='flex justify-between items-end mt-2.5 pt-2 border-t border-border'>
            {(n.traceTitle || n.data?.traceTitle) && <p className='text-[10px] text-muted font-bold truncate uppercase tracking-tight'>{t('common.trace', 'Trace')}: {n.traceTitle || n.data?.traceTitle}</p>}
            <button onClick={() => markAsRead(n.id)} className='text-[10px] font-bold text-accent hover:opacity-80 transition-all uppercase tracking-widest'>{t('notifications.read', 'Gelesen')}</button>
          </div>
        </div>
      ))}
    </div>,
    document.body
  );
}

/**
 * A side drawer component that displays the history of notifications for the current user.
 * Allows marking notifications as read and navigating to linked items (e.g., traces).
 * 
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.currentUser - The currently logged-in user.
 * @param {string} props.currentUser.name - User's name for filtering notifications.
 * @param {string} [props.currentUser.role] - User's role for role-based notifications.
 * @param {Function} props.onClose - Callback function to close the drawer.
 * @param {Function} props.onOpenItem - Callback to handle navigation when a notification item is clicked.
 * @returns {React.ReactPortal} A portal to document.body containing the history drawer.
 */
export function NotificationHistoryDrawer({
  currentUser,
  onClose,
  onOpenItem,
}: {
  currentUser: NotificationUser;
  onClose: () => void;
  onOpenItem: (id: string, type: string, entityType?: string) => void;
}) {
  const { t } = useTranslation();
  const activeTenantId = currentUser?.tenantId;
  const [searchQuery, setSearchQuery] = useState('');
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Expanded history item: full text via chevron toggle (same pattern as
  // the toast). stopPropagation prevents the row's open-item click.
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  // Activity filter: which category of notifications to show (null = all).
  // Local UI state on purpose — the filter is view-scoped, not persisted.
  const [activityFilter, setActivityFilter] = useState<ActivityCategory | null>(null);
  const [filterBarOpen, setFilterBarOpen] = useState(false);

  // Mention-name variants this user can be reached under (stable via useMemo —
  // primitives only, so the query key doesn't churn every render).
  const currentUserName = currentUser?.name;
  const currentUserRole = currentUser?.role;
  const possibleNames = useMemo(() => {
    if (!currentUserName) return [];
    const nameLower = currentUserName.toLowerCase();
    const nameSpace = currentUserName.split(" ")[0].toLowerCase();
    const nameDot = currentUserName.split(".")[0].toLowerCase();
    const roleLower = (currentUserRole || "").toLowerCase();
    return [...new Set([nameDot, nameLower, nameSpace, roleLower])].filter(Boolean);
  }, [currentUserName, currentUserRole]);

  // Server state via TanStack Query — key starts with 'notifications', so the
  // centralized realtimeStore invalidations (and every mutation below) refresh it.
  // This restores the live history updates lost in the Firestore migration.
  const { data: history = [] } = useQuery({
    queryKey: ['notifications', activeTenantId, possibleNames],
    queryFn: supabaseQuery(db => db.from('notifications')
      .select('*')
      .eq('tenant_id', activeTenantId)
      .in('mentioned_name', possibleNames)
      .order('created_at', { ascending: false })
      .limit(50)),
    enabled: !!currentUser?.name && !!activeTenantId && possibleNames.length > 0,
  });

  const queryClient = useQueryClient();

  /**
   * Marks a specific notification as read (single id) — invalidates the
   * ['notifications'] cache on success.
   */
  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await markNotificationRead(activeTenantId, id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  /** Marks all given notification ids as read in one batched update. */
  const markAllReadMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await markNotificationsRead(activeTenantId, ids);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  /**
   * Marks a specific notification as read. Stops event propagation
   * if triggered from a nested click event.
   *
   * @param {string} id - The notification ID.
   * @param {React.MouseEvent} [e] - Optional mouse event to stop propagation.
   */
  const markAsRead = (id: string, e?: React.MouseEvent) => {
    if (e && e.stopPropagation) e.stopPropagation();
    markAsReadMutation.mutate(id);
  };

  /**
   * Marks all unread notifications in the current history view as read.
   * One batched update (.in) instead of N parallel requests; the mutation
   * invalidates the cache, which re-renders the drawer with read state.
   */
  const markALLAsRead = () => {
    const unreadIds = history.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length > 0) markAllReadMutation.mutate(unreadIds);
  };

  const hideForMeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data: current } = await fetchNotificationById(activeTenantId, id);
      const currentDeletedBy = current?.data?.deletedBy || [];
      return updateNotificationDeletedBy(activeTenantId, id, {
        ...(current?.data || {}), deletedBy: [...currentDeletedBy, currentUser.name],
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  /**
   * Dismisses a specific notification for the current user.
   *
   * @param {string} id - The notification ID.
   * @param {React.MouseEvent} [e] - Optional mouse event to stop propagation.
   */
  const deleteNotification = (id: string, e?: React.MouseEvent) => {
    if (e && e.stopPropagation) e.stopPropagation();
    hideForMeMutation.mutate(id);
    setSelectedIds(prev => prev.filter(x => x !== id));
  };

  /**
   * Dismisses all selected notifications for the current user.
   */
  const deleteSelected = () => {
    selectedIds.forEach(id => hideForMeMutation.mutate(id));
    setSelectedIds([]);
    setIsSelectMode(false);
  };

  const adminDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await deleteNotifications(activeTenantId, ids);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  /**
   * Permanently deletes a notification for ALL users (Admin only).
   *
   * @param {string} id - The notification ID.
   * @param {React.MouseEvent} [e] - Optional mouse event to stop propagation.
   */
  const adminDeleteNotification = (id: string, e?: React.MouseEvent) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (window.confirm(t('notifications.confirmDeleteAll', 'Möchtest du diese Benachrichtigung wirklich für ALLE Benutzer endgültig löschen?'))) {
      adminDeleteMutation.mutate([id]);
      setSelectedIds(prev => prev.filter(x => x !== id));
    }
  };

  /**
   * Permanently deletes all selected notifications for ALL users (Admin only).
   */
  const adminDeleteSelected = () => {
    if (window.confirm(t('notifications.confirmDeleteAllMany', 'Möchtest du diese {{count}} Benachrichtigung(en) wirklich für ALLE Benutzer endgültig löschen?', { count: selectedIds.length }))) {
      adminDeleteMutation.mutate(selectedIds);
      setSelectedIds([]);
      setIsSelectMode(false);
    }
  };

  /**
   * Toggles the selection of a notification ID.
   * 
   * @param {string} id - The Firestore document ID.
   */
  const toggleSelect = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  /**
   * Toggles selecting all notifications in the current filtered view.
   */
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredHistory.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredHistory.map(n => n.id));
    }
  };

  /**
   * Formats a Firestore timestamp or JS Date into a human-readable German date string.
   * 
   * @param {Object|Date} timestamp - The timestamp object from Firestore or a Date object.
   * @returns {string} Formatted date string (e.g., "17.05.2026, 14:30 Uhr").
   */
  const formatTime = (timestamp: unknown) => {
    if (!timestamp) return t('notifications.justNow', 'Gerade eben');
    const toDateFn = (timestamp as { toDate?: unknown }).toDate;
    const d = typeof toDateFn === 'function' ? (timestamp as { toDate: () => Date }).toDate() : new Date(timestamp as string | number | Date);
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) + " " + t('common.clock', 'Uhr');
  };

  // Search filter (existing behaviour). useMemo so the categoryCounts
  // memo below has a stable dependency (react-hooks/preserve-manual-
  // memoization: the React Compiler lint rule rejects arrays/objects
  // created during render as memo deps).
  const searchFiltered = useMemo(() => history.filter(n => {
    const deletedBy = n.deletedBy || n.data?.deletedBy;
    if (deletedBy && deletedBy.includes(currentUser.name)) return false;
    const q = searchQuery.toLowerCase();
    const textMatch = (n.text || n.body || n.data?.text) ? (n.text || n.body || n.data?.text || '').toLowerCase().includes(q) : false;
    const userMatch = (n.fromUserName || n.data?.fromUserName) ? (n.fromUserName || n.data?.fromUserName || '').toLowerCase().includes(q) : false;
    const traceMatch = (n.traceTitle || n.data?.traceTitle) ? (n.traceTitle || n.data?.traceTitle || '').toLowerCase().includes(q) : false;
    return textMatch || userMatch || traceMatch;
  }), [history, searchQuery, currentUser.name]);

  // Activity filter: categories are resolved once per row via
  // getActivityCategory (pure fn — safe to call again in the counts memo).
  const filteredHistory = activityFilter
    ? searchFiltered.filter(n => getActivityCategory(n) === activityFilter)
    : searchFiltered;

  // Per-category counts (chips badges). Counts use the search-filtered
  // list so a badge always shows what the chip WOULD show when clicked.
  const categoryCounts = useMemo(() => {
    const counts: Record<ActivityCategory, number> = {
      trace: 0, 'chat-reply': 0, lost_and_found: 0, ticket: 0, storno: 0, hr: 0, other: 0,
    };
    for (const n of searchFiltered) counts[getActivityCategory(n)] += 1;
    return counts;
  }, [searchFiltered]);

  const isAdmin = currentUser?.role === 'admin';

  return createPortal(
    <div className='fixed inset-0 z-999999 flex justify-end'>
      <div className='absolute inset-0 bg-fg/20 animate-in fade-in' onClick={onClose}></div>

      <div className='relative w-full max-w-sm bg-bg h-full shadow-2xl flex flex-col animate-in slide-in-from-right border-l border-border'>
        <div className='p-6 border-b border-border bg-surface shrink-0 flex justify-between items-center'>
          <div className='flex items-center gap-2'>
            <Bell className='text-accent' size={20} />
            <h3 className='font-bold text-lg text-fg display-font'>{t('notifications.title', 'Benachrichtigungen')}</h3>
          </div>
          <button onClick={onClose} className='p-1 hover:bg-bg rounded-xl text-muted hover:text-fg transition-all'><X size={20} /></button>
        </div>

        {/* Search & Actions Panel */}
        <div className='p-4 border-b border-border bg-surface/50 shrink-0 space-y-3'>
          <div className='relative'>
            <Search className='absolute left-3 top-2.5 text-muted' size={16} />
            <input
              type='text'
              placeholder={t('common.search', 'Suchen') + '...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='w-full pl-9 pr-9 py-2 bg-surface border border-border rounded-xl text-xs font-medium placeholder:text-muted focus:outline-hidden focus:border-accent transition-all text-fg'
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className='absolute right-3 top-2.5 text-muted hover:text-fg transition-all'
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Activity filter bar: collapsible to keep the drawer compact.
             Chips show per-category counts (search-filtered scope) and the
             active chip gets its category color. */}
          {filterBarOpen && (
            <div className='flex flex-wrap gap-1.5'>
              {Object.entries(ACTIVITY_CATEGORIES).map(([cat, cfg]) => {
                const key = cat as ActivityCategory;
                const isActive = activityFilter === key;
                return (
                  <button
                    key={key}
                    data-testid={"activity-chip-" + key}
                    onClick={() => setActivityFilter(isActive ? null : key)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all border ${isActive
                      ? cfg.activeClasses
                      : 'bg-surface border-border text-muted hover:text-fg'}`}
                  >
                    <cfg.Icon size={10} />
                    <span>{t(cfg.labelKey)}</span>
                    <span className={`ml-0.5 px-1 rounded-full ${isActive ? 'bg-white/25' : 'bg-bg'}`}>
                      {categoryCounts[key]}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className='flex justify-between items-center text-xs'>
            {isSelectMode ? (
              <div className='flex items-center gap-2 w-full justify-between'>
                <button
                  onClick={toggleSelectAll}
                  className='flex items-center gap-1.5 font-bold text-muted hover:text-fg transition-all shrink-0'
                >
                  {selectedIds.length === filteredHistory.length && filteredHistory.length > 0 ? (
                    <CheckSquare size={16} className='text-accent' />
                  ) : (
                    <Square size={16} />
                  )}
                  <span>{t('common.allWithCount', 'Alle ({{count}})', { count: selectedIds.length })}</span>
                </button>
                <div className='flex gap-1.5 flex-wrap justify-end'>
                  {selectedIds.length > 0 && (
                    <>
                      <button
                        onClick={deleteSelected}
                        className='flex items-center gap-1 font-bold text-white bg-muted px-2 py-1 rounded-lg hover:opacity-90 transition-all uppercase tracking-widest text-[9px]'
                        title={t('notifications.hideForMe', 'Für mich ausblenden')}
                      >
                        <Trash2 size={10} /> {t('notifications.forMe', 'Für mich')}
                      </button>
                      {isAdmin && (
                        <button
                          onClick={adminDeleteSelected}
                          className='flex items-center gap-1 font-bold text-white bg-err px-2 py-1 rounded-lg hover:opacity-90 transition-all uppercase tracking-widest text-[9px]'
                          title={t('notifications.deleteForAll', 'Für alle löschen')}
                        >
                          <Trash2 size={10} /> {t('notifications.forAllAdmin', 'Für alle (Admin)')}
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => {
                      setIsSelectMode(false);
                      setSelectedIds([]);
                    }}
                    className='font-bold text-muted hover:text-fg bg-surface border border-border px-2 py-1 rounded-lg transition-all uppercase tracking-widest text-[9px]'
                  >
                    {t('common.done', 'Fertig')}
                  </button>
                </div>
              </div>
            ) : (
              <div className='flex items-center justify-between w-full'>
                <div className='flex items-center gap-1.5 min-w-0'>
                  <button
                    onClick={() => setFilterBarOpen(o => !o)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-all border uppercase tracking-widest text-[10px] font-bold shrink-0 ${filterBarOpen || activityFilter
                      ? 'bg-accent-muted text-accent border-accent/40'
                      : 'bg-surface border-border text-muted hover:text-fg'}`}
                    title={t('notifications.activityFilterToggle', 'Nach Ereignistyp filtern')}
                  >
                    <SlidersHorizontal size={11} />
                    {t('notifications.activityFilterToggle', 'Filter')}
                  </button>
                  {activityFilter && (
                    <button
                      onClick={() => setActivityFilter(null)}
                      className='flex items-center gap-1 bg-accent-muted text-accent px-2 py-1 rounded-lg uppercase tracking-widest text-[10px] font-bold min-w-0 max-w-[130px]'
                      title={t('notifications.activityFilterClear', 'Filter aufheben')}
                    >
                      <span className='truncate'>{t(ACTIVITY_CATEGORIES[activityFilter].labelKey)}</span>
                      <X size={10} />
                    </button>
                  )}
                </div>
                {filteredHistory.some(n => !n.read) && (
                  <button onClick={markALLAsRead} className='text-[10px] font-bold text-accent hover:opacity-80 bg-accent-muted px-2 py-1 rounded-lg transition-all uppercase tracking-widest'>
                    {t('notifications.markAllRead', 'Alle gelesen')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className='flex-1 overflow-y-auto p-4 space-y-3'>
          <div className='flex justify-between items-center mb-2 px-1'>
            <span className='text-[10px] font-bold text-muted uppercase tracking-wider'>{t('notifications.subtitle', 'Deine Erwähnungen & Alerts')}</span>
          </div>

          {filteredHistory.length === 0 ? (
            <p className='text-center text-muted text-sm py-10 font-medium'>{t('notifications.empty', 'Keine Benachrichtigungen vorhanden')}</p>
          ) : (
            filteredHistory.map(n => {
              const isUnread = !n.read;
              const isSelected = selectedIds.includes(n.id);
              return (
                <div
                  key={n.id}
                  onClick={(e) => {
                    if (isSelectMode) {
                      toggleSelect(n.id);
                    } else {
                      const traceId = n.traceId || n.data?.traceId;
                      const nType = n.type || n.data?.type || 'trace';
                      const entityType = n.data?.entityType;
                      // Storno conflicts carry reservationId (not traceId) —
                      // route them to the reservations table with the guest
                      // profile deep link (useDeepLinkReservation handles
                      // out-of-window ids via pms.fetchReservationById).
                      const reservationId = n.data?.reservationId;
                      // Ticket-reply notifications (api/admin.js
                      // notifyTicketCreatorOfReply) carry the id under
                      // data.ticketId, NOT traceId — without this branch the
                      // row was only marked read and never opened the ticket.
                      const ticketId = n.data?.ticketId;
                      if (nType === 'support-ticket-reply' && ticketId) {
                        onOpenItem(ticketId, nType, entityType);
                      } else if (nType === 'storno-conflict' && reservationId) {
                        onOpenItem(reservationId, nType, entityType);
                      } else if (traceId) {
                        onOpenItem(traceId, nType, entityType);
                      }
                      if (isUnread) markAsRead(n.id, e);
                    }
                  }}
                  className={`relative p-4 rounded-2xl border transition-all cursor-pointer hover:shadow-xl flex gap-3 ${isSelectMode ? (isSelected ? "border-accent bg-accent-muted/10" : "border-border bg-bg/50") : (isUnread ? "bg-surface border-accent/30 shadow-lg shadow-(--accent)/5" : "bg-bg/50 border-border opacity-70")}`}
                >
                  {isSelectMode && (
                    <div className='flex items-center shrink-0'>
                      {isSelected ? (
                        <CheckSquare size={18} className='text-accent' />
                      ) : (
                        <Square size={18} className='text-muted' />
                      )}
                    </div>
                  )}

                  <div className='flex-1 min-w-0 relative'>
                    {isUnread && !isSelectMode && <div className='absolute top-1 right-1 w-2.5 h-2.5 bg-accent rounded-full animate-pulse'></div>}
                    <p className='text-[10px] font-bold text-muted mb-1 uppercase tracking-widest'>{formatTime(n.created_at)}</p>
                    <p className={`text-sm ${isUnread ? "text-fg font-bold" : "text-muted font-medium"}`}>
                      {n.fromUserName || n.data?.fromUserName || t('common.system', 'System')}:
                    </p>
                    <div className='flex items-start gap-1 mt-1' onClick={e => e.stopPropagation()}>
                      <p className={`text-xs text-muted italic font-medium flex-1 ${expandedItemId === n.id ? '' : 'line-clamp-2'}`}>"{n.text || n.body || n.data?.text}"</p>
                      {(n.text || n.body || n.data?.text || '').length > 90 && (
                        <button
                          onClick={() => setExpandedItemId(expandedItemId === n.id ? null : n.id)}
                          className='text-muted hover:text-fg transition-all shrink-0 mt-0.5 cursor-pointer'
                          title={expandedItemId === n.id ? t('notifications.collapse') : t('notifications.expand')}
                        >
                          {expandedItemId === n.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      )}
                    </div>
                    <div className='mt-2 pt-3 border-t border-border flex justify-between items-center'>
                      {(n.traceTitle || n.data?.traceTitle) && <span className='text-[10px] font-bold text-muted truncate max-w-[150px] uppercase tracking-tight'>{t('common.trace', 'Trace')}: {n.traceTitle || n.data?.traceTitle}</span>}
                      <div className='flex items-center gap-2 ml-auto shrink-0'>
                        {isUnread && !isSelectMode && (
                          <button onClick={(e) => markAsRead(n.id, e)} className='text-[10px] font-bold text-accent hover:opacity-80 bg-accent-muted px-2 py-1 rounded-lg transition-all flex items-center gap-1 uppercase tracking-widest'>
                            <CheckCircle2 size={12} /> {t('notifications.read', 'Gelesen')}
                          </button>
                        )}
                        {!isSelectMode && (
                          <div className='flex items-center gap-1'>
                            <button
                              onClick={(e) => deleteNotification(n.id, e)}
                              className='p-1 hover:bg-surface rounded-lg text-muted hover:text-fg transition-all'
                              title={t('notifications.hideForMe', 'Für mich ausblenden')}
                            >
                              <Trash2 size={13} />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={(e) => adminDeleteNotification(n.id, e)}
                                className='p-1 hover:bg-err-muted rounded-lg text-err hover:opacity-85 transition-all'
                                title={t('notifications.deleteForAllAdmin', 'Für alle löschen (Admin)')}
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}